import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { ConfigService } from '../../../services/config.service';
import { TopicCombinationService } from '../../../services/topic-combination.service';
import { WritingHistoryService } from '../../../services/writing-history.service';
import { WritingSessionService } from '../../../services/writing-session.service';
import type { TopicCombination } from '../../../models';

const TASK_WORD_LIMITS = [
  { min: 60, max: 120 },
  { min: 120, max: 150 },
  { min: 150, max: 180 }
] as const;

const TOTAL_MINUTES = 60;

const FRENCH_SPECIAL_CHARS = [
  ['à', 'â', 'ä'],
  ['é', 'è', 'ê', 'ë'],
  ['î', 'ï'],
  ['ô', 'ö'],
  ['ù', 'û', 'ü'],
  ['ç'],
  ['œ', 'æ'],
  ['«', '»', "'", '–']
] as const;

const MIN_YEAR = 2025;
const MIN_MONTH = 1;

@Component({
  selector: 'app-writing-exam',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './writing-exam.component.html',
  styleUrl: './writing-exam.component.scss',
  imports: [FormsModule]
})
export class WritingExamComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly config = inject(ConfigService);
  private readonly combinationService = inject(TopicCombinationService);
  private readonly session = inject(WritingSessionService);
  private readonly writingHistory = inject(WritingHistoryService);

  protected readonly combination = signal<TopicCombination | null>(null);
  protected readonly loading = signal(true);
  protected readonly submitting = signal(false);
  protected readonly submitError = signal<string | null>(null);
  protected readonly outOfRange = signal(false);
  protected readonly activeTaskIndex = signal(0);
  protected readonly answers = signal<string[]>(['', '', '']);
  protected readonly timeRemainingSeconds = signal(TOTAL_MINUTES * 60);
  protected readonly isTimerRunning = signal(true);

  protected readonly activeTask = computed(() => {
    const c = this.combination();
    const idx = this.activeTaskIndex();
    if (!c) return null;
    return [
      { prompt: c.tache1.prompt, documents: [] },
      { prompt: c.tache2.prompt, documents: [] },
      {
        prompt: c.tache3.prompt,
        documents: [c.tache3.document1, c.tache3.document2].filter(Boolean) as string[]
      }
    ][idx] ?? null;
  });

  protected readonly currentAnswer = computed(() => this.answers()[this.activeTaskIndex()]);
  protected readonly wordCount = computed(() => {
    const text = this.currentAnswer();
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  });
  protected readonly wordLimit = computed(() => TASK_WORD_LIMITS[this.activeTaskIndex()]);
  protected readonly isWithinLimit = computed(() => {
    const count = this.wordCount();
    const limit = this.wordLimit();
    return count >= limit.min && count <= limit.max;
  });
  protected readonly specialCharsUpperCase = signal(false);
  protected readonly frenchChars = computed(() => {
    const upper = this.specialCharsUpperCase();
    return FRENCH_SPECIAL_CHARS.map((row) =>
      row.map((c) => (upper ? c.toUpperCase() : c))
    );
  });
  protected readonly timerDisplay = computed(() => {
    const s = this.timeRemainingSeconds();
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  });

  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private redirectTimer: ReturnType<typeof setTimeout> | null = null;
  private timerStarted = false;

  ngOnInit(): void {
    this.config.fetchAllowCopyPaste();
    const params = this.route.snapshot.params;
    const year = Number(params['year']);
    const month = Number(params['month']);
    const combinationNumber = params['combinationNumber']
      ? Number(params['combinationNumber'])
      : undefined;

    if (!this.isWithinAllowedWindow(year, month)) {
      this.handleOutOfRange();
      return;
    }

    const navigationCombination = (history.state?.selectedCombination as TopicCombination | undefined) ?? null;
    const navigationAnswers = (history.state?.answers as string[] | undefined) ?? null;

    if (this.initializeFromSession(navigationCombination, navigationAnswers)) {
      this.loading.set(false);
      this.startTimer();
      return;
    }

    if (year && month) {
      this.combinationService
        .getCombination(year, month, combinationNumber)
        .then((c) => {
          if (c) {
            this.combination.set(c);
            this.session.saveSession(c, ['', '', '']);
            this.startTimer();
          }
          this.loading.set(false);
        })
        .catch(() => this.loading.set(false));
    } else {
      this.loading.set(false);
    }
  }

  protected setActiveTask(index: number): void {
    this.activeTaskIndex.set(index);
  }

  protected updateAnswer(value: string): void {
    this.answers.update((arr) => {
      const next = [...arr];
      next[this.activeTaskIndex()] = value;
      this.session.updateAnswers(next);
      return next;
    });
  }

  protected insertChar(char: string): void {
    this.answers.update((arr) => {
      const next = [...arr];
      const idx = this.activeTaskIndex();
      next[idx] = (next[idx] ?? '') + char;
      this.session.updateAnswers(next);
      return next;
    });
  }

  protected toggleSpecialCharsCase(): void {
    this.specialCharsUpperCase.update((v) => !v);
  }

  protected async submitExam(submissionSucceeded = true): Promise<void> {
    this.isTimerRunning.set(false);
    this.stopTimer();
    const c = this.combination();
    const answers = this.answers();

    if (!submissionSucceeded || !c) {
      this.router.navigate(['/writing']);
      return;
    }

    this.submitting.set(true);
    this.submitError.set(null);

    try {
      const record = await this.writingHistory.submitExam(c, [...answers]);
      this.session.clearSession();
      this.router.navigate(
        ['/writing/results', c.year, c.month, c.combinationNumber],
        { state: { combination: c, answers: [...answers], record } }
      );
    } catch {
      this.submitError.set('Something went wrong. Please try again or save your answer manually.');
    } finally {
      this.submitting.set(false);
    }
  }

  protected exitExam(): void {
    this.stopTimer();
    this.router.navigate(['/writing/topics']);
  }

  ngOnDestroy(): void {
    this.stopTimer();
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
      this.redirectTimer = null;
    }
  }

  private startTimer(): void {
    if (this.timerStarted) return;
    this.timerStarted = true;
    this.timerInterval = setInterval(() => {
      this.timeRemainingSeconds.update((s) => {
        if (s <= 0) {
          this.stopTimer();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private isWithinAllowedWindow(year: number, month: number): boolean {
    if (!Number.isFinite(year) || !Number.isFinite(month)) return false;
    if (month < 1 || month > 12) return false;

    const target = year * 100 + month;
    const min = MIN_YEAR * 100 + MIN_MONTH;

    const now = new Date();
    const current = now.getFullYear() * 100 + (now.getMonth() + 1);

    return target >= min && target <= current;
  }

  private handleOutOfRange(): void {
    this.outOfRange.set(true);
    this.loading.set(false);
    this.isTimerRunning.set(false);
    this.redirectTimer = setTimeout(() => {
      this.router.navigate(['/writing/topics']);
    }, 2000);
  }

  private initializeFromSession(
    navigationCombination: TopicCombination | null,
    navigationAnswers: string[] | null
  ): boolean {
    const stored = this.session.getSession();

    if (navigationCombination) {
      const answersToUse =
        navigationAnswers && navigationAnswers.length === 3
          ? navigationAnswers
          : stored && this.session.isSameCombination(navigationCombination, stored.selectedCombination)
            ? (stored.answers ?? ['', '', ''])
            : ['', '', ''];

      if (!stored || !this.session.isSameCombination(navigationCombination, stored.selectedCombination)) {
        this.session.saveSession(navigationCombination, answersToUse);
        this.combination.set(navigationCombination);
        this.answers.set(answersToUse);
        return true;
      }
      this.combination.set(stored.selectedCombination);
      this.answers.set(answersToUse);
      return true;
    }

    if (stored) {
      this.combination.set(stored.selectedCombination);
      this.answers.set(stored.answers ?? ['', '', '']);
      return true;
    }

    return false;
  }
}
